import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import type {
  AdCreativeAssetReference,
  AdCreativeItem,
} from '@segmently/schema-models/ad-creative';
import { localeDirection } from '@modules/shared-locales';
import { AD_CREATIVE_BATCH } from '../../selectors';
import type {
  AdCreativeApiClient,
  AdCreativeImageReviewComponentBindingView,
  AdCreativeImageReviewCheck,
  AdCreativeImageReviewSlotView,
  AdCreativeImageReviewTextObservation,
  AdCreativeImageReviewView,
} from '../../services/AdCreativeApiClient';

const SHA256_PATTERN = /^[a-f0-9]{64}$/i;
const OPAQUE_REVIEW_REF_PATTERN = /^adimgreview_[a-f0-9]{40}$/;
const IMAGE_REVIEW_CHECKS = ['ocr', 'brand', 'claims', 'safeZones'] as const;
const IMAGE_REVIEW_OPERATIONS = new Set(['localize_visible_text', 'regenerate']);
const IMAGE_REVIEW_STATUSES = new Set(['review_required', 'ready', 'stale', 'failed']);
const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_REVIEW_SLOTS = 50;
const MAX_TEXT_OBSERVATIONS = 50;
const MAX_DETAILS = 50;

export type AdCreativeImageReviewGateState =
  | 'loading'
  | 'passed'
  | 'stale'
  | 'empty'
  | 'error';

export interface AdCreativeImageReviewGate {
  state: AdCreativeImageReviewGateState;
  canAccept: boolean;
  reason: string;
}

interface ImageLocalizationReviewPanelProps {
  client: Pick<AdCreativeApiClient, 'getImageLocalizationReview'>;
  projectId: string;
  item: AdCreativeItem;
  targetLocale: string;
  currentSourceComponentHash?: string;
  componentStatus: string;
  binding?: AdCreativeImageReviewComponentBindingView;
  onGateChange: (gate: AdCreativeImageReviewGate) => void;
}

interface ReviewEvaluation {
  gate: AdCreativeImageReviewGate;
  requiredSlotKeys: string[];
  slotCurrent: Record<string, boolean>;
}

interface OcrDisplayObservation {
  text: string;
  confidence?: number;
  box?: AdCreativeImageReviewTextObservation['box'];
}

type TargetPreviewState = 'loading' | 'loaded' | 'error';

/**
 * Review-only projection for generated Segmently-copy images. This panel never
 * mutates provider data or treats verifier evidence as human approval.
 */
export function ImageLocalizationReviewPanel({
  client,
  projectId,
  item,
  targetLocale,
  currentSourceComponentHash,
  componentStatus,
  binding,
  onGateChange,
}: ImageLocalizationReviewPanelProps): React.ReactElement {
  const [reviews, setReviews] = useState<AdCreativeImageReviewView[]>([]);
  const [evaluation, setEvaluation] = useState<ReviewEvaluation | null>(null);
  const [evaluatedItemScope, setEvaluatedItemScope] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(binding));
  const [error, setError] = useState<string | null>(null);
  const [targetPreviewStates, setTargetPreviewStates] = useState<Record<string, TargetPreviewState>>({});
  const [targetPreviewAttempts, setTargetPreviewAttempts] = useState<Record<string, number>>({});
  const requestSequenceRef = useRef(0);
  const itemRef = useRef(item);
  itemRef.current = item;
  const onGateChangeRef = useRef(onGateChange);
  onGateChangeRef.current = onGateChange;
  const resolvedBinding = useMemo(
    () => parseAdCreativeImageReviewComponentBinding(binding),
    [binding],
  );
  const itemCurrencyScope = useMemo(
    () => imageItemCurrencyScope(item, targetLocale),
    [item, targetLocale],
  );

  const loadReview = useCallback(async (): Promise<void> => {
    const currentItem = itemRef.current;
    const currentItemScope = itemCurrencyScope;
    const sequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = sequence;
    setReviews([]);
    setEvaluation(null);
    setEvaluatedItemScope(null);
    setError(null);

    const requiredSlotKeys = generatedImageSlotKeys(currentItem, targetLocale);
    const boundJobIds = resolvedBinding
      ? Array.from(new Set(requiredSlotKeys
        .map((slotKey) => resolvedBinding.slots[slotKey]?.jobId)
        .filter((jobId): jobId is string => Boolean(jobId))))
      : [];
    if (boundJobIds.length === 0) {
      const nextEvaluation = evaluateImageReviews({
        reviews: [],
        binding: resolvedBinding,
        currentSourceComponentHash,
        componentStatus,
        projectId,
        item: currentItem,
        targetLocale,
      });
      setEvaluation(nextEvaluation);
      setEvaluatedItemScope(currentItemScope);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const responses = await Promise.all(boundJobIds.map((jobId) => client.getImageLocalizationReview(
        projectId,
        currentItem.id,
        jobId,
        targetLocale,
      )));
      if (sequence !== requestSequenceRef.current) return;
      const parsedReviews = responses.map((response) => {
        if (!response.success) {
          throw new Error(response.error || 'Image verification evidence could not be loaded.');
        }
        const parsed = parseAdCreativeImageReviewView(response.data?.review);
        if (!parsed) {
          throw new Error('Image verification evidence is malformed or contains an unsupported projection.');
        }
        return parsed;
      });
      if (new Set(parsedReviews.map((review) => review.jobId)).size !== parsedReviews.length) {
        throw new Error('Image verification evidence returned duplicate job projections.');
      }
      const nextEvaluation = evaluateImageReviews({
        reviews: parsedReviews,
        binding: resolvedBinding,
        currentSourceComponentHash,
        componentStatus,
        projectId,
        item: currentItem,
        targetLocale,
      });
      setReviews(parsedReviews);
      setEvaluation(nextEvaluation);
      setEvaluatedItemScope(currentItemScope);
    } catch (nextError) {
      if (sequence !== requestSequenceRef.current) return;
      const message = nextError instanceof Error ? nextError.message : String(nextError);
      setError(message);
    } finally {
      if (sequence === requestSequenceRef.current) setLoading(false);
    }
  }, [client, componentStatus, currentSourceComponentHash, itemCurrencyScope, projectId, resolvedBinding, targetLocale]);

  useEffect(() => {
    void loadReview();
    return () => {
      requestSequenceRef.current += 1;
    };
  }, [loadReview]);

  const slots = useMemo(() => {
    if (!resolvedBinding) return [];
    const currentRefs = new Set(Object.values(resolvedBinding.slots).map((slot) => slot.reviewRef));
    return reviews.flatMap((review) => review.slots).filter((slot) => currentRefs.has(slot.reviewRef));
  }, [resolvedBinding, reviews]);
  const scopedEvidenceGate = evaluation && evaluatedItemScope === itemCurrencyScope
    ? evaluation.gate
    : evaluation
      ? { state: 'loading' as const, canAccept: false, reason: 'Refreshing exact current image evidence.' }
      : null;
  const effectiveGate = useMemo(() => gateWithTargetPreviews(
    scopedEvidenceGate,
    slots,
    targetPreviewStates,
  ), [scopedEvidenceGate, slots, targetPreviewStates]);

  useEffect(() => {
    if (loading) {
      onGateChangeRef.current({
        state: 'loading',
        canAccept: false,
        reason: 'Loading exact AI image verification evidence.',
      });
      return;
    }
    if (error) {
      onGateChangeRef.current({ state: 'error', canAccept: false, reason: error });
      return;
    }
    if (effectiveGate) onGateChangeRef.current(effectiveGate);
  }, [effectiveGate, error, loading]);

  const recordTargetPreview = useCallback((reviewRef: string, state: TargetPreviewState) => {
    setTargetPreviewStates((current) => current[reviewRef] === state
      ? current
      : { ...current, [reviewRef]: state });
  }, []);

  const retryTargetPreview = useCallback((reviewRef: string) => {
    setTargetPreviewStates((current) => ({ ...current, [reviewRef]: 'loading' }));
    setTargetPreviewAttempts((current) => ({ ...current, [reviewRef]: (current[reviewRef] ?? 0) + 1 }));
  }, []);

  return (
    <Box
      component="section"
      aria-labelledby="ad-creative-image-review-title"
      data-testid={AD_CREATIVE_BATCH.drawerImageReviewPanel}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25, minWidth: 0 }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography id="ad-creative-image-review-title" variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
            AI image evidence
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            color={effectiveGate?.state === 'passed' ? 'success' : effectiveGate?.state === 'stale' ? 'warning' : 'default'}
            label={effectiveGate?.state === 'passed' ? 'Checks passed' : 'Acceptance blocked'}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Compare the exact Segmently source and localized copy. Verifier checks unlock human acceptance; they never approve, publish, or change Meta/Facebook assets.
        </Typography>

        {loading && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            aria-live="polite"
            data-testid={AD_CREATIVE_BATCH.drawerImageReviewLoading}
          >
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Loading exact image evidence…
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" data-testid={AD_CREATIVE_BATCH.drawerImageReviewError}>
            <Stack spacing={0.75} alignItems="flex-start">
              <span>{error}</span>
              <Button
                size="small"
                variant="outlined"
                onClick={() => void loadReview()}
                disabled={loading}
                data-testid={AD_CREATIVE_BATCH.drawerImageReviewRetry}
              >
                Retry evidence check
              </Button>
            </Stack>
          </Alert>
        )}

        {!loading && !error && evaluation?.gate.state === 'empty' && (
          <Alert severity="warning" data-testid={AD_CREATIVE_BATCH.drawerImageReviewEmpty}>
            {evaluation.gate.reason}
          </Alert>
        )}

        {!loading && !error && evaluation?.gate.state === 'stale' && (
          <Alert severity="warning" data-testid={AD_CREATIVE_BATCH.drawerImageReviewStale}>
            {evaluation.gate.reason} Generate or load evidence for the exact current Segmently-copy revision before accepting images.
          </Alert>
        )}

        {!loading && !error && effectiveGate?.state === 'loading' && evaluation?.gate.state === 'passed' && (
          <Alert severity="info" aria-live="polite">
            Loading each exact target image before acceptance can be unlocked.
          </Alert>
        )}

        {!loading && !error && effectiveGate?.state === 'passed' && (
          <Alert severity="success">
            Immutable evidence matches every current AI-generated image and all four checks passed. Human acceptance is still required.
          </Alert>
        )}

        {slots.map((slot) => (
          <ImageReviewSlot
            key={`${slot.slotKey}:${slot.reviewRef}`}
            item={item}
            targetLocale={targetLocale}
            slot={slot}
            current={evaluation?.slotCurrent[slot.slotKey] === true}
            previewState={evaluation?.slotCurrent[slot.slotKey] === true
              ? targetPreviewStates[slot.reviewRef] ?? 'loading'
              : undefined}
            previewAttempt={targetPreviewAttempts[slot.reviewRef] ?? 0}
            onPreviewStateChange={evaluation?.slotCurrent[slot.slotKey] === true
              ? (state) => recordTargetPreview(slot.reviewRef, state)
              : undefined}
            onRetryPreview={evaluation?.slotCurrent[slot.slotKey] === true
              ? () => retryTargetPreview(slot.reviewRef)
              : undefined}
          />
        ))}
      </Stack>
    </Box>
  );
}

function ImageReviewSlot({
  item,
  targetLocale,
  slot,
  current,
  previewState,
  previewAttempt,
  onPreviewStateChange,
  onRetryPreview,
}: {
  item: AdCreativeItem;
  targetLocale: string;
  slot: AdCreativeImageReviewSlotView;
  current: boolean;
  previewState?: TargetPreviewState;
  previewAttempt: number;
  onPreviewStateChange?: (state: TargetPreviewState) => void;
  onRetryPreview?: () => void;
}): React.ReactElement {
  const sourceAsset = exactImageAsset(item, slot.slotKey, item.defaultLanguage);
  const checks = slot.verification.checks;
  return (
    <Box
      data-testid={AD_CREATIVE_BATCH.drawerImageReviewSlot(slot.slotKey)}
      sx={{ borderTop: 1, borderColor: 'divider', pt: 1, minWidth: 0 }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, overflowWrap: 'anywhere' }}>
            {slot.slotKey}
          </Typography>
          <Chip size="small" variant="outlined" label={operationLabel(slot.operation)} />
          <Chip
            size="small"
            variant="outlined"
            color={current ? 'success' : 'warning'}
            label={current ? 'Current evidence' : 'Stale evidence'}
          />
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
          <ImagePreview
            label={`Source · ${item.defaultLanguage}`}
            locale={item.defaultLanguage}
            url={reviewAssetUrl(sourceAsset)}
            testId={AD_CREATIVE_BATCH.drawerImageReviewSource(slot.slotKey)}
          />
          <ImagePreview
            label={`Localized · ${targetLocale}`}
            locale={targetLocale}
            url={slot.output.url}
            testId={AD_CREATIVE_BATCH.drawerImageReviewTarget(slot.slotKey)}
            expectedWidth={slot.output.width}
            expectedHeight={slot.output.height}
            loadState={previewState}
            attempt={previewAttempt}
            stateKey={slot.slotKey}
            onLoadStateChange={onPreviewStateChange}
            onRetry={onRetryPreview}
          />
        </Box>

        <Box component="section" aria-label={`${slot.slotKey} verification checks`}>
          <Stack spacing={0.5}>
            {IMAGE_REVIEW_CHECKS.map((checkKey) => {
              const check = checks[checkKey];
              return (
                <Box
                  key={checkKey}
                  data-testid={AD_CREATIVE_BATCH.drawerImageReviewCheck(slot.slotKey, checkKey)}
                  sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', minWidth: 0 }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    color={check.passed ? 'success' : 'error'}
                    label={check.passed ? 'Pass' : 'Fail'}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {checkLabel(checkKey)}
                      {typeof check.confidence === 'number' ? ` · ${Math.round(check.confidence * 100)}%` : ''}
                    </Typography>
                    {check.details?.length ? (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ overflowWrap: 'anywhere' }}>
                        {check.details.join(' · ')}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>

        <OcrTextProjection
          slotKey={slot.slotKey}
          sourceLocale={item.defaultLanguage}
          targetLocale={targetLocale}
          expected={slot.verification.expectedText}
          observed={slot.verification.observedText}
        />

        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          Evidence {slot.reviewRef} · {slot.lineage.provider}/{slot.lineage.model} → {slot.verification.provider}/{slot.verification.model}
          {' · '}
          source SHA-256 {slot.lineage.sourceSha256} · output SHA-256 {slot.output.sha256}
        </Typography>
      </Stack>
    </Box>
  );
}

function ImagePreview({
  label,
  locale,
  url,
  testId,
  expectedWidth,
  expectedHeight,
  loadState,
  attempt = 0,
  stateKey,
  onLoadStateChange,
  onRetry,
}: {
  label: string;
  locale: string;
  url?: string;
  testId: string;
  expectedWidth?: number;
  expectedHeight?: number;
  loadState?: TargetPreviewState;
  attempt?: number;
  stateKey?: string;
  onLoadStateChange?: (state: TargetPreviewState) => void;
  onRetry?: () => void;
}): React.ReactElement {
  return (
    <Box data-testid={testId} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        lang={locale}
        dir={localeDirection(locale)}
        sx={{ display: 'block', mb: 0.5, overflowWrap: 'anywhere' }}
      >
        {label}
      </Typography>
      {url ? (
        <Box
          key={`${url}:${attempt}`}
          component="img"
          src={url}
          alt={label}
          loading="lazy"
          decoding="async"
          onLoad={(event: React.SyntheticEvent<HTMLImageElement>) => {
            if (!onLoadStateChange) return;
            const image = event.currentTarget;
            const dimensionsMatch = expectedWidth === undefined
              || expectedHeight === undefined
              || (image.naturalWidth === expectedWidth && image.naturalHeight === expectedHeight);
            onLoadStateChange(dimensionsMatch ? 'loaded' : 'error');
          }}
          onError={() => onLoadStateChange?.('error')}
          sx={{ display: 'block', width: '100%', height: 132, objectFit: 'contain', bgcolor: 'action.hover', borderRadius: 1 }}
        />
      ) : (
        <Box sx={{ height: 132, display: 'grid', placeItems: 'center', bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
          <Typography variant="caption" color="text.secondary" align="center">
            Preview unavailable
          </Typography>
        </Box>
      )}
      {loadState === 'loading' && (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mt: 0.5 }}
          aria-live="polite"
          data-testid={stateKey ? AD_CREATIVE_BATCH.drawerImageReviewTargetLoading(stateKey) : undefined}
        >
          <CircularProgress size={12} />
          <Typography variant="caption" color="text.secondary">Verifying exact target preview…</Typography>
        </Stack>
      )}
      {loadState === 'error' && (
        <Alert
          severity="warning"
          sx={{ mt: 0.5 }}
          data-testid={stateKey ? AD_CREATIVE_BATCH.drawerImageReviewTargetError(stateKey) : undefined}
        >
          <Stack spacing={0.5} alignItems="flex-start">
            <span>The exact target image failed to load or its pixel dimensions do not match the evidence.</span>
            {onRetry && (
              <Button
                size="small"
                variant="outlined"
                onClick={onRetry}
                data-testid={stateKey ? AD_CREATIVE_BATCH.drawerImageReviewTargetRetry(stateKey) : undefined}
              >
                Retry target preview
              </Button>
            )}
          </Stack>
        </Alert>
      )}
    </Box>
  );
}

function OcrTextProjection({
  slotKey,
  sourceLocale,
  targetLocale,
  expected,
  observed,
}: {
  slotKey: string;
  sourceLocale: string;
  targetLocale: string;
  expected: string[];
  observed: AdCreativeImageReviewTextObservation[];
}): React.ReactElement {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
      <OcrTextColumn
        title="Expected target text"
        locale={targetLocale}
        observations={expected.map((text) => ({ text }))}
        testId={AD_CREATIVE_BATCH.drawerImageReviewExpectedText(slotKey)}
      />
      <OcrTextColumn
        title="Observed output text"
        locale={targetLocale || sourceLocale}
        observations={observed}
        testId={AD_CREATIVE_BATCH.drawerImageReviewObservedText(slotKey)}
      />
    </Box>
  );
}

function OcrTextColumn({
  title,
  locale,
  observations,
  testId,
}: {
  title: string;
  locale: string;
  observations?: OcrDisplayObservation[];
  testId: string;
}): React.ReactElement {
  return (
    <Box data-testid={testId} sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>{title}</Typography>
      {observations?.length ? observations.map((observation, index) => (
        <Typography
          key={`${observation.text}:${index}`}
          variant="caption"
          display="block"
          lang={locale}
          dir={localeDirection(locale)}
          sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
        >
          {observation.text}
          {typeof observation.confidence === 'number' ? ` · ${Math.round(observation.confidence * 100)}%` : ''}
          {observation.box
            ? ` · region x ${formatCoordinate(observation.box.x)}, y ${formatCoordinate(observation.box.y)}, w ${formatCoordinate(observation.box.width)}, h ${formatCoordinate(observation.box.height)}`
            : ''}
        </Typography>
      )) : (
        <Typography variant="caption" color="text.secondary" display="block">
          Sanitized OCR text was not included in this projection.
        </Typography>
      )}
    </Box>
  );
}

export function evaluateImageReviews(input: {
  reviews: AdCreativeImageReviewView[];
  binding: AdCreativeImageReviewComponentBindingView | null;
  currentSourceComponentHash?: string;
  componentStatus: string;
  projectId: string;
  item: AdCreativeItem;
  targetLocale: string;
}): ReviewEvaluation {
  const requiredSlotKeys = generatedImageSlotKeys(input.item, input.targetLocale);
  if (requiredSlotKeys.length === 0) {
    return {
      gate: { state: 'empty', canAccept: true, reason: 'No AI-generated target images require verifier evidence.' },
      requiredSlotKeys,
      slotCurrent: {},
    };
  }
  if (!input.binding) {
    return {
      gate: {
        state: 'empty',
        canAccept: false,
        reason: 'No current per-component image evidence binding exists for these generated images.',
      },
      requiredSlotKeys,
      slotCurrent: {},
    };
  }

  if (input.componentStatus !== 'review_required' && input.componentStatus !== 'ready') {
    return {
      gate: {
        state: 'stale',
        canAccept: false,
        reason: 'The image component is stale or not reviewable against the current source images.',
      },
      requiredSlotKeys,
      slotCurrent: {},
    };
  }

  const boundSlotKeys = Object.keys(input.binding.slots).sort();
  const bindingCurrent = input.binding.bindingVersion === 1
    && isSha(input.currentSourceComponentHash)
    && input.binding.sourceComponentHash === normalizedSha(input.currentSourceComponentHash)
    && input.binding.targetLocale === input.targetLocale
    && boundSlotKeys.length === requiredSlotKeys.length
    && boundSlotKeys.every((slotKey, index) => slotKey === requiredSlotKeys[index]);
  const reviewsByJob = new Map(input.reviews.map((review) => [review.jobId, review]));
  const slotCurrent: Record<string, boolean> = {};
  for (const slotKey of requiredSlotKeys) {
    const slotBinding = input.binding.slots[slotKey];
    const review = slotBinding ? reviewsByJob.get(slotBinding.jobId) : undefined;
    const reviewSlot = review?.slots.find((slot) => (
      slot.slotKey === slotKey && slot.reviewRef === slotBinding?.reviewRef
    ));
    const reviewCurrent = Boolean(review
      && review.component === 'images'
      && review.creativeItemId === input.item.id
      && input.item.projectId === input.projectId
      && review.targetLocale === input.targetLocale
      && review.sourceComponentHash === input.binding.sourceComponentHash
      && review.sourceComponentHash === slotBinding?.sourceComponentHash
      && review.sourceComponentHash === normalizedSha(input.currentSourceComponentHash)
      && review.inputManifestHash === slotBinding?.inputManifestHash
      && (review.reviewStatus === 'review_required' || review.reviewStatus === 'ready'));
    slotCurrent[slotKey] = Boolean(
      bindingCurrent
      && slotBinding
      && reviewCurrent
      && reviewSlot
      && isCurrentPassingSlot(input.item, input.targetLocale, reviewSlot, slotBinding),
    );
  }
  const failed = requiredSlotKeys.filter((slotKey) => slotCurrent[slotKey] !== true);
  if (bindingCurrent && failed.length === 0) {
    const relevantJobIds = new Set(requiredSlotKeys.map((slotKey) => input.binding!.slots[slotKey].jobId));
    const canAccept = Array.from(relevantJobIds).every((jobId) => reviewsByJob.get(jobId)?.reviewStatus === 'review_required');
    return {
      gate: {
        state: 'passed',
        canAccept,
        reason: canAccept
          ? 'All current AI-generated image slots have matching immutable passing evidence.'
          : 'All checks passed and this image component is already accepted.',
      },
      requiredSlotKeys,
      slotCurrent,
    };
  }
  const reason = !bindingCurrent
    ? 'The per-component image binding does not exactly match the current generated slots, locale, or image-component hash.'
    : `Image evidence is missing, stale, or failed for: ${failed.join(', ')}.`;
  return {
    gate: { state: 'stale', canAccept: false, reason },
    requiredSlotKeys,
    slotCurrent,
  };
}

function isCurrentPassingSlot(
  item: AdCreativeItem,
  targetLocale: string,
  slot: AdCreativeImageReviewSlotView,
  binding: AdCreativeImageReviewComponentBindingView['slots'][string],
): boolean {
  const source = exactImageAsset(item, slot.slotKey, item.defaultLanguage);
  const target = exactImageAsset(item, slot.slotKey, targetLocale);
  if (!source || !target || slot.immutable !== true) return false;
  if (binding.reviewRef !== slot.reviewRef
    || binding.operation !== slot.operation
    || binding.outputAssetId !== target.assetId
    || normalizedSha(binding.outputSha256) !== normalizedSha(target.sha256)
    || source.assetId !== slot.lineage.sourceAssetId
    || normalizedSha(source.sha256) !== normalizedSha(slot.lineage.sourceSha256)
    || target.assetId !== slot.output.assetId
    || normalizedSha(target.sha256) !== normalizedSha(slot.output.sha256)
    || target.original !== slot.output.url
    || target.mimeType !== slot.output.mimeType
    || target.width !== slot.output.width
    || target.height !== slot.output.height) {
    return false;
  }
  return IMAGE_REVIEW_CHECKS.every((key) => slot.verification.checks[key].passed === true);
}

function gateWithTargetPreviews(
  evidenceGate: AdCreativeImageReviewGate | null,
  slots: AdCreativeImageReviewSlotView[],
  previewStates: Record<string, TargetPreviewState>,
): AdCreativeImageReviewGate | null {
  if (!evidenceGate || evidenceGate.state !== 'passed' || !evidenceGate.canAccept) return evidenceGate;
  const failed = slots.filter((slot) => previewStates[slot.reviewRef] === 'error');
  if (failed.length > 0) {
    return {
      state: 'error',
      canAccept: false,
      reason: `Exact target preview failed for: ${failed.map((slot) => slot.slotKey).join(', ')}.`,
    };
  }
  const pending = slots.filter((slot) => previewStates[slot.reviewRef] !== 'loaded');
  if (pending.length > 0) {
    return {
      state: 'loading',
      canAccept: false,
      reason: `Loading exact target preview for: ${pending.map((slot) => slot.slotKey).join(', ')}.`,
    };
  }
  return evidenceGate;
}

function generatedImageSlotKeys(item: AdCreativeItem, targetLocale: string): string[] {
  return Object.entries(item.localizedMedia ?? {})
    .filter(([, slot]) => slot.mediaKind === 'image' || slot.mediaKind === 'poster')
    .filter(([, slot]) => exactLocaleValue(slot.translations ?? {}, targetLocale)?.provenance === 'generated')
    .map(([slotKey]) => slotKey)
    .sort();
}

function imageItemCurrencyScope(item: AdCreativeItem, targetLocale: string): string {
  const slots = Object.entries(item.localizedMedia ?? {})
    .filter(([, slot]) => slot.mediaKind === 'image' || slot.mediaKind === 'poster')
    .map(([slotKey, slot]) => ({
      slotKey,
      mediaKind: slot.mediaKind,
      source: imageEntryCurrency(exactLocaleValue(slot.translations ?? {}, item.defaultLanguage)),
      target: imageEntryCurrency(exactLocaleValue(slot.translations ?? {}, targetLocale)),
    }))
    .sort((left, right) => left.slotKey.localeCompare(right.slotKey));
  return JSON.stringify({
    itemId: item.id,
    projectId: item.projectId,
    sourceLocale: item.defaultLanguage,
    targetLocale,
    slots,
  });
}

function imageEntryCurrency(value: { provenance?: string; disabled?: boolean; asset?: AdCreativeAssetReference } | undefined) {
  return value ? {
    provenance: value.provenance,
    disabled: value.disabled === true,
    asset: value.asset ? {
      assetId: value.asset.assetId,
      original: value.asset.original,
      mimeType: value.asset.mimeType,
      width: value.asset.width,
      height: value.asset.height,
      sha256: value.asset.sha256,
    } : null,
  } : null;
}

function exactImageAsset(item: AdCreativeItem, slotKey: string, locale: string): AdCreativeAssetReference | undefined {
  const slot = item.localizedMedia?.[slotKey];
  if (!slot || (slot.mediaKind !== 'image' && slot.mediaKind !== 'poster')) return undefined;
  return exactLocaleValue(slot.translations ?? {}, locale)?.asset;
}

function exactLocaleValue<T>(record: Record<string, T>, locale: string): T | undefined {
  return Object.prototype.hasOwnProperty.call(record, locale) ? record[locale] : undefined;
}

function reviewAssetUrl(asset: AdCreativeAssetReference | undefined): string | undefined {
  const value = asset?.original ?? asset?.small;
  return isHttpUrl(value) ? value : undefined;
}

function operationLabel(operation: AdCreativeImageReviewSlotView['operation']): string {
  if (operation === 'localize_visible_text') return 'Visible text localized';
  return 'Regenerated';
}

function checkLabel(check: typeof IMAGE_REVIEW_CHECKS[number]): string {
  if (check === 'safeZones') return 'Safe zones';
  if (check === 'ocr') return 'OCR and target text';
  return check.charAt(0).toUpperCase() + check.slice(1);
}

function normalizedSha(value: string | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}

/** Runtime allowlist for the v3 image-component binding returned by localization index reads. */
export function parseAdCreativeImageReviewComponentBinding(
  value: unknown,
): AdCreativeImageReviewComponentBindingView | null {
  const binding = asRecord(value);
  const slots = asRecord(binding?.slots);
  if (!binding
    || binding.bindingVersion !== 1
    || !isSha(binding.sourceComponentHash)
    || !isSafeIdentifier(binding.targetLocale)
    || !slots
    || Object.keys(slots).length > MAX_REVIEW_SLOTS) {
    return null;
  }
  const parsedSlots: AdCreativeImageReviewComponentBindingView['slots'] = {};
  for (const [slotKey, rawSlot] of Object.entries(slots)) {
    const slot = asRecord(rawSlot);
    if (!isSafeIdentifier(slotKey)
      || !slot
      || !isImageJobId(slot.jobId)
      || !isSha(slot.inputManifestHash)
      || !isSha(slot.sourceComponentHash)
      || !isOpaqueReviewRef(slot.reviewRef)
      || !IMAGE_REVIEW_OPERATIONS.has(String(slot.operation))
      || !isBoundedString(slot.outputAssetId)
      || !isSha(slot.outputSha256)) {
      return null;
    }
    parsedSlots[slotKey] = {
      jobId: slot.jobId as string,
      inputManifestHash: slot.inputManifestHash as string,
      sourceComponentHash: normalizedSha(slot.sourceComponentHash as string),
      reviewRef: slot.reviewRef as string,
      operation: slot.operation as AdCreativeImageReviewSlotView['operation'],
      outputAssetId: slot.outputAssetId as string,
      outputSha256: normalizedSha(slot.outputSha256 as string),
    };
  }
  return {
    bindingVersion: 1,
    sourceComponentHash: normalizedSha(binding.sourceComponentHash as string),
    targetLocale: binding.targetLocale as string,
    slots: parsedSlots,
  };
}

/** Strict, allowlisted parser for the bounded backend projection. */
export function parseAdCreativeImageReviewView(value: unknown): AdCreativeImageReviewView | null {
  const review = asRecord(value);
  if (!review
    || review.component !== 'images'
    || !IMAGE_REVIEW_STATUSES.has(String(review.reviewStatus))
    || !isImageJobId(review.jobId)
    || !isBoundedString(review.creativeItemId)
    || !isBoundedString(review.targetLocale)
    || !isSha(review.sourceComponentHash)
    || !isBoundedString(review.sourceRevision)
    || !isSha(review.inputManifestHash)
    || !Array.isArray(review.slots)
    || review.slots.length > MAX_REVIEW_SLOTS) {
    return null;
  }
  const slots = review.slots.map(parseSlot);
  if (slots.some((slot) => !slot)) return null;
  const typedSlots = slots as AdCreativeImageReviewSlotView[];
  if (new Set(typedSlots.map((slot) => slot.slotKey)).size !== typedSlots.length) return null;
  return {
    component: 'images',
    jobId: review.jobId as string,
    creativeItemId: review.creativeItemId as string,
    targetLocale: review.targetLocale as string,
    sourceComponentHash: normalizedSha(review.sourceComponentHash as string),
    sourceRevision: review.sourceRevision as string,
    inputManifestHash: review.inputManifestHash as string,
    reviewStatus: review.reviewStatus as AdCreativeImageReviewView['reviewStatus'],
    slots: typedSlots,
  };
}

function parseSlot(value: unknown): AdCreativeImageReviewSlotView | null {
  const slot = asRecord(value);
  const lineage = asRecord(slot?.lineage);
  const output = asRecord(slot?.output);
  const verification = asRecord(slot?.verification);
  const checks = asRecord(verification?.checks);
  if (!slot
    || !isBoundedString(slot.slotKey)
    || !IMAGE_REVIEW_OPERATIONS.has(String(slot.operation))
    || !isOpaqueReviewRef(slot.reviewRef)
    || slot.immutable !== true
    || !isStrictIsoDate(slot.createdAt)
    || !lineage
    || !isBoundedString(lineage.sourceAssetId)
    || !isSha(lineage.sourceSha256)
    || !isBoundedString(lineage.provider)
    || !isBoundedString(lineage.model)
    || !output
    || !isBoundedString(output.assetId)
    || !isHttpUrl(output.url)
    || !IMAGE_MIME_TYPES.has(String(output.mimeType))
    || !isPositiveInteger(output.width)
    || !isPositiveInteger(output.height)
    || !isSha(output.sha256)
    || !isNonNegativeInteger(output.byteSize)
    || !verification
    || !isBoundedString(verification.provider)
    || !isBoundedString(verification.model)
    || !isSha(verification.evaluationHash)
    || !checks) {
    return null;
  }
  const parsedChecks = Object.fromEntries(IMAGE_REVIEW_CHECKS.map((key) => [key, parseCheck(checks[key])])) as Record<
    typeof IMAGE_REVIEW_CHECKS[number],
    AdCreativeImageReviewCheck | null
  >;
  if (IMAGE_REVIEW_CHECKS.some((key) => !parsedChecks[key])) return null;
  const expectedText = optionalStringArray(verification.expectedText, MAX_TEXT_OBSERVATIONS, 500);
  const observedText = parseObservations(verification.observedText);
  if (!expectedText || !observedText) return null;
  return {
    slotKey: slot.slotKey as string,
    operation: slot.operation as AdCreativeImageReviewSlotView['operation'],
    reviewRef: slot.reviewRef as string,
    immutable: true,
    createdAt: slot.createdAt as string,
    lineage: {
      sourceAssetId: lineage.sourceAssetId as string,
      sourceSha256: normalizedSha(lineage.sourceSha256 as string),
      provider: lineage.provider as string,
      model: lineage.model as string,
    },
    output: {
      assetId: output.assetId as string,
      url: output.url as string,
      mimeType: output.mimeType as string,
      width: output.width as number,
      height: output.height as number,
      sha256: normalizedSha(output.sha256 as string),
      byteSize: output.byteSize as number,
    },
    verification: {
      provider: verification.provider as string,
      model: verification.model as string,
      evaluationHash: verification.evaluationHash as string,
      checks: {
        ocr: parsedChecks.ocr as AdCreativeImageReviewCheck,
        brand: parsedChecks.brand as AdCreativeImageReviewCheck,
        claims: parsedChecks.claims as AdCreativeImageReviewCheck,
        safeZones: parsedChecks.safeZones as AdCreativeImageReviewCheck,
      },
      expectedText,
      observedText,
    },
  };
}

function parseCheck(value: unknown): AdCreativeImageReviewCheck | null {
  const check = asRecord(value);
  if (!check || typeof check.passed !== 'boolean') return null;
  const confidence = optionalConfidence(check.confidence);
  const details = optionalStringArray(check.details, MAX_DETAILS, 2_000);
  if (confidence === null || details === null) return null;
  return {
    passed: check.passed,
    ...(confidence === undefined ? {} : { confidence }),
    ...(details === undefined ? {} : { details }),
  };
}

function parseObservations(value: unknown): AdCreativeImageReviewTextObservation[] | undefined | null {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length > MAX_TEXT_OBSERVATIONS) return null;
  const result: AdCreativeImageReviewTextObservation[] = [];
  for (const raw of value) {
    const observation = asRecord(raw);
    if (!observation || !isBoundedString(observation.text, 500)) return null;
    const confidence = optionalConfidence(observation.confidence);
    if (confidence === null || confidence === undefined) return null;
    const box = observation.box === undefined ? undefined : parseBox(observation.box);
    if (observation.box !== undefined && !box) return null;
    result.push({
      text: observation.text as string,
      confidence,
      ...(box ? { box } : {}),
    });
  }
  return result;
}

function parseBox(value: unknown): AdCreativeImageReviewTextObservation['box'] | null {
  const box = asRecord(value);
  if (!box || !isFiniteNumber(box.x) || !isFiniteNumber(box.y) || !isFiniteNumber(box.width) || !isFiniteNumber(box.height)) {
    return null;
  }
  const x = box.x as number;
  const y = box.y as number;
  const width = box.width as number;
  const height = box.height as number;
  if (x < 0 || y < 0 || width <= 0 || height <= 0 || x + width > 1.000001 || y + height > 1.000001) return null;
  return { x: box.x as number, y: box.y as number, width: box.width as number, height: box.height as number };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function isBoundedString(value: unknown, max = 1_000): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= max;
}

function isSafeIdentifier(value: unknown): value is string {
  return typeof value === 'string'
    && value.length <= 300
    && /^[A-Za-z0-9][A-Za-z0-9._:@-]*$/.test(value);
}

function isImageJobId(value: unknown): value is string {
  return isSafeIdentifier(value) && value.startsWith('adlocjob_images_');
}

function isOpaqueReviewRef(value: unknown): value is string {
  return typeof value === 'string' && OPAQUE_REVIEW_REF_PATTERN.test(value);
}

function isSha(value: unknown): value is string {
  return typeof value === 'string' && SHA256_PATTERN.test(value);
}

function isHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 2_000) return false;
  try {
    const parsed = new URL(value);
    return (parsed.protocol === 'https:' || parsed.protocol === 'http:')
      && !parsed.username
      && !parsed.password
      && !parsed.search
      && !parsed.hash;
  } catch {
    return false;
  }
}

function isStrictIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 100) return false;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString() === value;
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function optionalConfidence(value: unknown): number | undefined | null {
  if (value === undefined) return undefined;
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1 ? value : null;
}

function optionalStringArray(value: unknown, maxItems: number, maxChars: number): string[] | undefined | null {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length > maxItems || value.some((entry) => !isBoundedString(entry, maxChars))) return null;
  return value as string[];
}

function formatCoordinate(value: number): string {
  return value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}
